import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Cart context for Shopify Buy SDK integration
const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

// Helper to find the Shopify Buy SDK cart in localStorage
const findShopifyCart = () => {
  try {
    // The Shopify Buy SDK stores cart data with keys like "x16ha3-n1.myshopify.com.checkoutId"
    // and the actual cart/checkout data
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.includes('shopify') || key.includes('checkout'))) {
        const value = localStorage.getItem(key);
        if (value) {
          try {
            const parsed = JSON.parse(value);
            // Check if this looks like checkout/cart data
            if (parsed && parsed.lineItems) {
              return parsed;
            }
          } catch (e) {
            // Not JSON, skip
          }
        }
      }
    }
    
    // Also check for the standard Buy SDK storage pattern
    const keys = Object.keys(localStorage);
    for (const key of keys) {
      if (key.includes('myshopify.com')) {
        const value = localStorage.getItem(key);
        if (value) {
          try {
            const parsed = JSON.parse(value);
            if (parsed && (parsed.lineItems || parsed.lineItemCount !== undefined)) {
              return parsed;
            }
          } catch (e) {
            // Not JSON
          }
        }
      }
    }
  } catch (e) {
    console.warn('Error reading cart from localStorage:', e);
  }
  return null;
};

// Helper to get cart count from checkout data
const getCartCountFromCheckout = (checkout) => {
  if (!checkout) return 0;
  
  // Direct lineItemCount property
  if (typeof checkout.lineItemCount === 'number') {
    return checkout.lineItemCount;
  }
  
  // Sum up quantities from lineItems array
  if (checkout.lineItems && Array.isArray(checkout.lineItems)) {
    return checkout.lineItems.reduce((sum, item) => {
      const qty = item.quantity || 1;
      return sum + qty;
    }, 0);
  }
  
  // Check for lineItems.edges (GraphQL format)
  if (checkout.lineItems && checkout.lineItems.edges) {
    return checkout.lineItems.edges.reduce((sum, edge) => {
      const qty = edge.node?.quantity || 1;
      return sum + qty;
    }, 0);
  }
  
  return 0;
};

// Helper to get checkout URL
const getCheckoutUrl = (checkout) => {
  if (!checkout) return null;
  return checkout.webUrl || checkout.checkoutUrl || checkout.url || null;
};

export const CartProvider = ({ children }) => {
  const [cartCount, setCartCount] = useState(0);
  const [checkoutUrl, setCheckoutUrl] = useState(null);

  // Read cart state from Shopify Buy SDK
  const updateCartCount = useCallback(() => {
    let count = 0;
    let url = null;
    
    // Method 1: Check Shopify Buy UI instance on window
    if (window.ShopifyBuy && window.ShopifyBuy.UI) {
      const ui = window.ShopifyBuy.UI;
      // Try to access the cart from UI components
      if (ui.components && ui.components.cart && ui.components.cart[0]) {
        const cart = ui.components.cart[0];
        if (cart.model) {
          count = getCartCountFromCheckout(cart.model);
          url = getCheckoutUrl(cart.model);
        }
      }
    }
    
    // Method 2: Check localStorage for persisted cart
    if (count === 0) {
      const storedCart = findShopifyCart();
      if (storedCart) {
        count = getCartCountFromCheckout(storedCart);
        url = getCheckoutUrl(storedCart);
      }
    }
    
    // Method 3: Look for global cart reference that Buy SDK might expose
    if (count === 0 && window.shopifyCart) {
      count = getCartCountFromCheckout(window.shopifyCart);
      url = getCheckoutUrl(window.shopifyCart);
    }
    
    setCartCount(count);
    if (url) {
      setCheckoutUrl(url);
    }
  }, []);

  // Set up polling and event listeners
  useEffect(() => {
    // Initial check after a short delay (let Buy SDK initialize)
    const initialTimer = setTimeout(updateCartCount, 500);
    
    // Poll frequently for cart updates
    const interval = setInterval(updateCartCount, 500);
    
    // Listen for storage events (cross-tab cart updates)
    const handleStorage = () => {
      updateCartCount();
    };
    window.addEventListener('storage', handleStorage);
    
    // Listen for custom cart update events
    const handleCartUpdate = (event) => {
      if (event.detail && typeof event.detail.count === 'number') {
        setCartCount(event.detail.count);
      }
      if (event.detail && event.detail.checkoutUrl) {
        setCheckoutUrl(event.detail.checkoutUrl);
      }
      updateCartCount();
    };
    window.addEventListener('shopify-cart-update', handleCartUpdate);
    
    // Also observe DOM mutations for Shopify Buy SDK elements
    const observer = new MutationObserver(() => {
      updateCartCount();
    });
    observer.observe(document.body, { 
      childList: true, 
      subtree: true,
      attributes: true,
      attributeFilter: ['data-shopify-buy-ui']
    });
    
    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('shopify-cart-update', handleCartUpdate);
      observer.disconnect();
    };
  }, [updateCartCount]);

  // Function to manually set cart count
  const setCount = useCallback((count) => {
    setCartCount(count);
  }, []);

  // Function to open checkout
  const openCheckout = useCallback(() => {
    if (checkoutUrl) {
      window.location.href = checkoutUrl;
    } else {
      // Fallback: try to find checkout URL from localStorage
      const cart = findShopifyCart();
      const url = getCheckoutUrl(cart);
      if (url) {
        window.location.href = url;
      }
    }
  }, [checkoutUrl]);

  return (
    <CartContext.Provider value={{ 
      cartCount, 
      checkoutUrl, 
      setCount, 
      openCheckout, 
      updateCartCount 
    }}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
